import type {AppVersionPolicy} from '../../utils/AppVersion';
import type {PublicRollout} from '../../utils/Rollout';
import {TypedApiRequestParams} from '../types';

/**
 * `GET /api/app/flags`: the app configuration anyone may read, signed in or
 * out (2026-10-13 decision C), the same body for everyone.
 * - `flags`: `{name: enabled_globally}` for the backend's public allow-list,
 *   a flag whose row is missing left out. Today: `mobile_maplibre_off`, the
 *   mobile map engine's kill switch, read by builds before 1.65 only (MapLibre
 *   is the only engine since; builds before 1.64 read only this key).
 * - `rollouts` (#379, backend #269): `{name: {enabled, rollout_pct}}` for the
 *   same flags; each device computes its own bucket (`utils/Rollout`).
 * - `app_versions`: `{ios|android: {minimum, recommended}}`.
 */
type PublicFlagsResponse = {
  flags: Record<string, boolean>;
  rollouts?: Record<string, PublicRollout>;
  app_versions?: Record<string, AppVersionPolicy>;
};

type AppPlatformWithPolicy = 'ios' | 'android';

/** The public app configuration as the store keeps it. */
type PublicConfig = {
  flags: Record<string, boolean>;
  rollouts: Record<string, PublicRollout>;
  appVersions: Partial<Record<AppPlatformWithPolicy, AppVersionPolicy>>;
};

const POLICY_PLATFORMS: AppPlatformWithPolicy[] = ['ios', 'android'];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const versionOrNull = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

/**
 * Read a `GET /api/app/flags` body. `null` when it has no `flags` object
 * (malformed: keep what is stored). Inside a good body, a bad entry is dropped
 * on its own: a non-boolean flag, a rollout whose share is not an integer in
 * 0-100, a platform we do not know. A version that is not a string is `null`.
 */
const parsePublicConfig = (data: unknown): PublicConfig | null => {
  if (!isPlainObject(data) || !isPlainObject(data.flags)) {
    return null;
  }
  const flags: Record<string, boolean> = {};
  for (const [name, value] of Object.entries(data.flags)) {
    if (typeof value === 'boolean') {
      flags[name] = value;
    }
  }
  const rollouts: Record<string, PublicRollout> = {};
  if (isPlainObject(data.rollouts)) {
    for (const [name, value] of Object.entries(data.rollouts)) {
      if (
        isPlainObject(value) &&
        typeof value.enabled === 'boolean' &&
        typeof value.rollout_pct === 'number' &&
        Number.isInteger(value.rollout_pct) &&
        value.rollout_pct >= 0 &&
        value.rollout_pct <= 100
      ) {
        rollouts[name] = {
          enabled: value.enabled,
          rollout_pct: value.rollout_pct,
        };
      }
    }
  }
  const appVersions: PublicConfig['appVersions'] = {};
  if (isPlainObject(data.app_versions)) {
    for (const platform of POLICY_PLATFORMS) {
      const policy = data.app_versions[platform];
      if (isPlainObject(policy)) {
        appVersions[platform] = {
          minimum: versionOrNull(policy.minimum),
          recommended: versionOrNull(policy.recommended),
        };
      }
    }
  }
  return {flags, rollouts, appVersions};
};

// The backend authenticates nobody here and ignores any credentials. Note
// that useApiClient does not read `authRequired`: a signed-in app still
// sends its Bearer token (and may refresh it first, single-flight with /me).
const getPublicFlags = (): TypedApiRequestParams<PublicFlagsResponse> => ({
  url: 'app/flags',
  method: 'GET',
  authRequired: false,
});

export type {AppPlatformWithPolicy, PublicConfig, PublicFlagsResponse};
export {parsePublicConfig};

export default {
  getPublicFlags,
};
